import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import cn from "classnames";
import { nanoid } from "nanoid";
import { tagular } from "@cohesion/tagular";
import { useInView } from "react-intersection-observer";

import { useBestInTravel } from "@context/bestInTravel/2025/bestInTravelContext";
import { getThemeClasses } from "@utils/bestInTravel/2025/getThemeClasses";

import { BIT_PROP_TYPES } from "@constants/best-in-travel/2025/proptypes";

import { CaretRight, ArrowLeftFill } from "@icons/";
import { Ad } from "@components/ad";
import { SailyPin } from "@pageComponents/best-in-travel-2025/saily/sailyPin";

import { Articles, Blockquote, OL, P } from "./mods";
import { ContentSubmenu } from "./contentSubmenu";
import { ShareIcon } from "./shareIcon";

const renderEditorial = (arr, themeClasses) => {
  const map = {
    blockquote: Blockquote,
    ol: OL,
    p: P,
  };

  return arr.map(item => {
    const Component = map[item.type];
    // TODO - improve key
    return (
      <Component
        key={nanoid(6)}
        data={item.children}
        themeClasses={themeClasses}
      />
    );
  });
};

const categorySingularMapping = {
  cities: "city",
  regions: "region",
  countries: "country",
};

const getSingularCategory = category =>
  categorySingularMapping[category] || category;

export function DestinationContent({
  destination,
  index,
  totalDestinations,
  mediaCredits = "Lonely Planet",
  activeMedia,
  mediaItems,
}) {
  const {
    setDestinationsMenuOpen,
    handleUpDownNavProvider,
    setShareDrawerOpen,
    setShareData,
  } = useBestInTravel();

  const themeClasses = getThemeClasses(destination.meta.category);
  const [intro, ...additional] = destination.editorial;
  const singularCategory = getSingularCategory(destination.meta.category);

  const NEXT = 1;
  const PREVIOUS = -1;

  const [collapsedContent, setCollapsedContent] = useState(true);

  const handleShareDrawer = () => {
    setShareData(destination);
    setShareDrawerOpen(true);
  };
  const { ref, inView } = useInView({
    threshold: 1,
    triggerOnce: true,
  });

  useEffect(() => {
    if (inView) {
      tagular("view", {
        webElement: {
          location: "BITDESTINATION",
          elementType: "SECTION",
          position: "0",
          name: destination?.name,
        },
      });
    }
  }, [inView, destination]);

  // Advertisement visibility observer
  const { ref: adRef, inView: adInView } = useInView({
    threshold: 0,
    triggerOnce: true,
    skip: !inView, // Start observing only if InView is true
  });

  return (
    <div
      className={cn(
        "absolute z-20 bottom-0 flex flex-col w-full sm:h-screen-dvh sm:relative sm:w-1/3 sm:rounded-none sm:order-1 sm:bg-white no-scrollbar",
        collapsedContent
          ? `${themeClasses.background} pb-4 border-none rounded-t-lg overscroll-y-none sm:overscroll-y-auto sm:h-screen-dvh sm:overflow-y-scroll`
          : "bg-white z-20 mt-4 h-screen-dvh overflow-y-scroll"
      )}
      ref={ref}
    >
      {/* CREDITS AND MEDIA INDICATORS - MOBILE */}
      {inView && (
        <div className="absolute flex items-center justify-between w-full px-4 mx-auto -mt-8 h-content lg:hidden">
          <div
            className={cn(
              "flex items-center h-5 gap-2 px-2 rounded-pill bg-transparent-60",
              mediaItems === 1 && "invisible"
            )}
          >
            {Array.from({ length: mediaItems }).map((_, i) => (
              <div
                className={cn(
                  "w-2 h-2 rounded-full ring-1 ring-inset ring-white ring-opacity-60",
                  activeMedia === i && "bg-white"
                )}
                key={nanoid(6)}
              />
            ))}
          </div>
          {mediaCredits && (
            <div className="relative bg-transparent-60 text-white text-[10px] leading-none h-5 rounded-pill w-content flex items-center px-2">
              {mediaCredits}
            </div>
          )}
        </div>
      )}

      {/* NAVIGATION - DESKTOP */}
      <div
        className={cn(
          "hidden justify-between px-6 p-6 rounded-none lg:px-12 sm:flex sm:min-h-[88px]",
          themeClasses.background,
          collapsedContent ? "rounded-t-lg sm:rounded-t-none" : "rounded-none"
        )}
      >
        <button
          type="button"
          className="bg-white border-none btn text-blue"
          onClick={() => {
            setDestinationsMenuOpen(true);
            tagular("click", {
              actionOutcome: "OPEN",
              outboundUrl: "null",
              webElement: {
                location: "BIT_Overview",
                elementType: "BUTTON",
                text: "View All",
                name: `${destination?.name}`,
                position: "0",
              },
            });
          }}
        >
          View All
        </button>

        <div className="flex gap-2">
          <button
            disabled={index === totalDestinations - 1}
            type="button"
            onClick={() => {
              handleUpDownNavProvider(index, NEXT);
              tagular("click", {
                actionOutcome: "SLIDE",
                outboundUrl: "null",
                webElement: {
                  location: `BIT_Overview_${singularCategory}`,
                  elementType: "BUTTON",
                  text: "NEXT ARROW",
                  name: `${destination?.name}`,
                  position: "2",
                },
              });
            }}
            className="disabled:opacity-50"
          >
            <CaretRight className="text-3xl rotate-90 text-blue" />
          </button>
          <button
            disabled={index === 0}
            type="button"
            onClick={() => {
              handleUpDownNavProvider(index, PREVIOUS);
              tagular("click", {
                actionOutcome: "SLIDE",
                outboundUrl: "null",
                webElement: {
                  location: `BIT_Overview_${singularCategory}`,
                  elementType: "BUTTON",
                  text: "PREV ARROW",
                  name: `${destination?.name}`,
                  position: "1",
                },
              });
            }}
            className="disabled:opacity-50"
          >
            <CaretRight className="text-3xl -rotate-90 text-blue" />
          </button>
        </div>
      </div>

      {/* ARTICLES CONTENT */}
      <div
        className={cn(
          "flex flex-col justify-between px-6 pt-6 sm:pt-0 lg:px-12 ",
          collapsedContent ? "rounded-t-lg sm:rounded-none" : "rounded-none",
          themeClasses.background
        )}
      >
        {/* NAVIGATION - MOBILE */}
        <button
          type="button"
          className={cn(
            "items-center gap-1 pb-4 text-sm font-bold text-blue sm:hidden",
            collapsedContent ? "flex" : "hidden"
          )}
          onClick={() => {
            setDestinationsMenuOpen(true);
            tagular("click", {
              actionOutcome: "OPEN",
              outboundUrl: "null",
              webElement: {
                location: "BIT_Overview_Mobile",
                elementType: "BUTTON",
                text: "View All",
                name: `${destination?.name}`,
                position: "0",
              },
            });
          }}
        >
          <ArrowLeftFill className="text-sm" /> View All
        </button>

        <h2
          className={cn(
            "font-rama leading-none font-extrabold flex-grow text-2xl lg:text-5xl uppercase line-clamp-4",
            !collapsedContent && "text-4xl",
            themeClasses.textSubMenuItems
          )}
        >
          {destination?.name}
        </h2>
        <h3
          className={cn(
            "font-neue font-extrabold uppercase",
            themeClasses.textSubMenuItems
          )}
        >
          {singularCategory}
          <span className="px-2 font-light">|</span>
          {destination.meta.parent}
        </h3>
      </div>
      {/* READ MORE - MOBILE */}
      <div
        className={cn(
          "container relative flex items-center justify-center gap-2 pt-4 sm:hidden sm:h-0",
          themeClasses.background
        )}
      >
        <button
          type="button"
          onClick={() => setCollapsedContent(!collapsedContent)}
          className={cn(
            "w-full h-10 rounded-pill text-sm font-semibold border",
            collapsedContent
              ? "border-black-200 text-blue"
              : "bg-blue  border-blue text-white"
          )}
        >
          {collapsedContent ? "Read more" : "Read less"}
        </button>

        <button
          type="button"
          className="flex items-center justify-center flex-shrink-0 w-10 h-10 border rounded-full border-black-200 text-blue"
          onClick={() => handleShareDrawer(destination)}
        >
          <ShareIcon />
        </button>
      </div>
      {/* BODY */}
      <div className={cn(collapsedContent ? "hidden sm:block" : "h-auto")}>
        {/* INTRO DESCRIPTION */}
        <div
          className={cn(
            "px-6 pt-6 pb-10 rounded-b-lg lg:px-12",
            themeClasses.background
          )}
        >
          {renderEditorial([intro])}

          {/* PARTNER CONTENT - SAILY */}
          {destination?.saily !== false && (
            <div className="flex items-center gap-3 pt-8">
              <SailyPin className="w-6 h-6 text-blue" />
              <div className="flex flex-col gap-3">
                <div className="leading-none font-neue">
                  {/* eslint-disable-next-line react/jsx-no-target-blank */}
                  <a
                    href={destination?.saily || "https://saily.com/"}
                    target="_blank"
                    className="border-b border-blue"
                    rel="noopener nofollow"
                    onClick={() => {
                      tagular("click", {
                        actionOutcome: "External Link",
                        outboundUrl: destination?.saily || "https://saily.com/",
                        webElement: {
                          location: "BIT_Overview",
                          elementType: "LINK",
                          text: "Saily eSim can keep you connected here.",
                          name: destination?.name,
                          position: "0",
                        },
                      });
                    }}
                  >
                    Saily eSim
                  </a>{" "}
                  can keep you connected here.
                </div>
              </div>
            </div>
          )}
        </div>

        {/* HIGHLIGHTS AND QUOTE */}
        <div className="py-6 bg-white">
          {renderEditorial(additional, themeClasses)}
        </div>

        {/* RELATED ARTICLES */}
        {destination.articles && inView && (
          <Articles className="bg-white" slugs={destination.articles} />
        )}

        {/* ADVERTISEMENT */}
        {inView && (
          <div ref={adRef}>
            {adInView && (
              <Ad
                className="px-6 py-6 lg:px-12"
                path="best-in-travel/sidebar"
                placement="railShort"
                bgColor="bg-transparent"
              />
            )}
          </div>
        )}

        {/* CONTENT SUBMENU */}
        <ContentSubmenu destination={destination} />
      </div>
    </div>
  );
}

DestinationContent.propTypes = {
  destination: BIT_PROP_TYPES.destination.isRequired,
  index: PropTypes.number.isRequired,
  totalDestinations: PropTypes.number.isRequired,
  mediaCredits: PropTypes.string,
  activeMedia: PropTypes.number.isRequired,
  mediaItems: PropTypes.number.isRequired,
};
